import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { PIPELINE_STAGE_LABELS, STAGE_THRESHOLD_METRICS, saveStageThresholds } from "../services/jobService";
import "./RecruiterJobs.css";

// Per-stage auto-advance rules: when a candidate's result for their current
// stage meets every minimum set here, the server moves them to the next stage
// and emails that stage's invite (pipeline_sync.py). Candidates below any
// minimum stay put, flagged "Below threshold", for the recruiter to review.
// An empty minimum means "don't check this metric".

const STAGE_HINTS = {
  resume: "Checked as soon as each resume is scored.",
  form: "No score — advances as soon as the candidate submits the form.",
  mcq: "Checked when the candidate submits the MCQ test.",
  coding: "Checked once the coding submission is graded.",
  both: "Checked once both the MCQ and the graded coding results are in.",
  ai: "Checked when the AI interview report is ready."
};

export function defaultThresholds(stages, stageConfig) {
  const out = {};
  for (const stage of stages) {
    if (!STAGE_THRESHOLD_METRICS[stage]) continue;
    out[stage] = { autoAdvance: false, min: stage === "resume" ? { score: stageConfig?.minScore ?? 60 } : {} };
  }
  return out;
}

export default function StageThresholdsEditor({ stages, thresholds, onChange, inline = false }) {
  const editable = stages.filter((s) => STAGE_THRESHOLD_METRICS[s]);

  const update = (stage, patch) => {
    const current = thresholds[stage] || { autoAdvance: false, min: {} };
    onChange({ ...thresholds, [stage]: { ...current, ...patch } });
  };
  const setMin = (stage, metric, raw) => {
    const min = { ...((thresholds[stage] || {}).min || {}) };
    if (raw === "") delete min[metric];
    else min[metric] = Math.max(0, Math.min(100, Number(raw)));
    update(stage, { min });
  };

  return (
    <div className={`stage-thresholds${inline ? " stage-thresholds--inline" : ""}`}>
      {editable.map((stage) => {
        const cfg = thresholds[stage] || { autoAdvance: false, min: {} };
        const next = stages[stages.indexOf(stage) + 1];
        return (
          <div key={stage} className={`stage-threshold-card${cfg.autoAdvance ? " on" : ""}`}>
            <div className="stage-threshold-head">
              <div>
                <strong>{PIPELINE_STAGE_LABELS[stage]}</strong>
                <small className="rd-muted">{STAGE_HINTS[stage]}</small>
              </div>
              <label className="stage-threshold-toggle">
                <input
                  type="checkbox"
                  checked={!!cfg.autoAdvance}
                  onChange={(e) => update(stage, { autoAdvance: e.target.checked })}
                />
                <span>Auto-advance to {PIPELINE_STAGE_LABELS[next] || "next stage"} &amp; send invite</span>
              </label>
            </div>
            {STAGE_THRESHOLD_METRICS[stage].length > 0 && (
              <div className="stage-threshold-metrics">
                {STAGE_THRESHOLD_METRICS[stage].map(([metric, label]) => (
                  <label key={metric} className="stage-threshold-metric">
                    <span>{label} ≥</span>
                    <input
                      type="number" min={0} max={100} placeholder="—"
                      value={cfg.min?.[metric] ?? ""}
                      onChange={(e) => setMin(stage, metric, e.target.value)}
                      disabled={!cfg.autoAdvance}
                    />
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function StageThresholdsModal({ open, job, onClose, onSaved }) {
  const stages = job?.pipelineStages || [];
  const [thresholds, setThresholds] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setThresholds({ ...defaultThresholds(stages, job?.stageConfig), ...(job?.stageConfig?.thresholds || {}) });
  }, [open, job]); // eslint-disable-line

  if (!open) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveStageThresholds(job.jobId, thresholds);
      toast.success("Thresholds saved — existing candidates are being re-checked now.");
      onSaved?.(saved);
      onClose();
    } catch (err) {
      toast.error(err.message || "Unable to save thresholds");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="recruiter-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="recruiter-modal recruiter-modal--wide" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="recruiter-modal-header">
          <div>
            <h2>Stage thresholds</h2>
            <p>Candidates who meet every minimum move on automatically and get the next invite by email.</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">x</button>
        </div>
        <StageThresholdsEditor stages={stages} thresholds={thresholds} onChange={setThresholds} />
        <div className="recruiter-modal-actions stage-thresholds-modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>Cancel</button>
          <button className="primary-button" type="button" disabled={saving} onClick={handleSave}>
            {saving ? "Saving…" : "Save thresholds"}
          </button>
        </div>
      </div>
    </div>
  );
}
